// src/pages/QualityStops.jsx
//
// "Quality Stops" — fourth tab on /admin/cron-health (global_admin only).
//
// The queue of leads whose AI was killed by quality-monitor. Before this page the only
// signals were an email to ALERT_TO and somebody noticing: Carmen, Daniel and Atul were
// all found by inspection, not surfaced by the system. quality-monitor sets no
// handoff_triggered / hot_reason / attention_reasons, so a killed lead appeared on no
// dashboard anywhere, and 39 logged failures read exactly the same as zero.
//
// ADMIN-ONLY BY DECISION (Tom, 2026-08-01). A quality kill must never appear on the
// tenant's own dashboard. DashboardV2.jsx is deliberately untouched.
//
// Grouped by LEAD, not by failure row: one bad conversation can trip several rules
// (lead 378f3ab4 logged three in one evening) and that is one thing to go look at.
//
// TIMEZONE RULE: every timestamp renders in America/New_York via fmtET(). No UTC.
// No auto-refresh: this is a review queue, and a 30s refresh under a half-typed note
// is hostile. Same reasoning as the Disputed Records tab.

import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldAlert, RefreshCw, ExternalLink, ChevronDown, ChevronRight,
  CheckCircle, XCircle, Undo2, AlertTriangle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchWithCsrf } from '../lib/fetchWithCsrf';

function fmtET(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    hour12: true, timeZoneName: 'short',
  }).format(d);
}

function relTime(iso) {
  if (!iso) return '';
  const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 0 || isNaN(secs)) return '';
  const m = Math.round(secs / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

const FILTERS = [
  { key: 'pinned', label: 'Needs review' },
  { key: 'acknowledged', label: 'Acknowledged' },
  { key: 'all', label: 'All' },
];

// ─── Acknowledge control ──────────────────────────────────────────────────────

function AckPanel({ stop, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const isPinned = stop.failures.some((f) => f.pinned_to_dashboard);

  async function send(repin) {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetchWithCsrf('/api/admin/quality-stops/acknowledge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: stop.lead_id,
          note: note.trim() || undefined,
          pinned: repin ? true : undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setOpen(false);
      setNote('');
      onDone();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!isPinned) {
    return (
      <button
        onClick={() => send(true)}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        <Undo2 className="w-4 h-4" />
        {busy ? 'Working...' : 'Put back in the queue'}
      </button>
    );
  }

  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-300 bg-white text-indigo-700 text-sm font-medium hover:bg-indigo-50"
        >
          <CheckCircle className="w-4 h-4" />
          Mark reviewed
        </button>
        <span className="text-xs text-gray-500">
          Clears all {stop.failures.length} flag{stop.failures.length === 1 ? '' : 's'} on this
          lead. Does not turn the AI back on.
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-300 bg-gray-50 p-3 space-y-2.5">
      <div className="flex items-start gap-2 text-sm text-gray-800">
        <AlertTriangle className="w-4 h-4 text-gray-500 mt-0.5 flex-none" />
        <div>
          Marking reviewed only clears this from the queue. The lead's AI stays off, because
          turning it back on is a call about this specific conversation and belongs on the
          lead page.
        </div>
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="What did you do? (e.g. replied by hand, AI back on / lead was already dead)"
        className="w-full text-sm rounded-lg border border-gray-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      {err && <div className="text-xs text-red-700">{err}</div>}
      <div className="flex items-center gap-2">
        <button
          onClick={() => send(false)}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
        >
          {busy ? 'Saving...' : 'Confirm reviewed'}
        </button>
        <button
          onClick={() => { setOpen(false); setErr(null); }}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── One stopped lead ─────────────────────────────────────────────────────────

function StopRow({ stop, onDone }) {
  const isPinned = stop.failures.some((f) => f.pinned_to_dashboard);
  const [open, setOpen] = useState(isPinned);
  const Chevron = open ? ChevronDown : ChevronRight;
  const ack = stop.failures.find((f) => f.acknowledged_at);

  return (
    <div className={`bg-white border rounded-xl overflow-hidden ${
      stop.ai_back_on === false ? 'border-amber-300' : 'border-gray-200'
    }`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
      >
        <Chevron className="w-4 h-4 text-gray-400 flex-none" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">
              {stop.lead?.name || (stop.lead_missing ? 'Lead deleted' : 'Unknown lead')}
            </span>
            {stop.lead?.phone && (
              <span className="text-xs text-gray-500">{stop.lead.phone}</span>
            )}
            {stop.ai_back_on === false ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-200 bg-amber-100 text-amber-800 text-xs font-semibold">
                <XCircle className="w-3.5 h-3.5" /> AI still off
              </span>
            ) : stop.ai_back_on === true ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-green-200 bg-green-100 text-green-800 text-xs font-semibold">
                <CheckCircle className="w-3.5 h-3.5" /> AI back on
              </span>
            ) : null}
            {!isPinned && (
              <span className="text-xs px-2 py-0.5 rounded-full border border-gray-200 bg-gray-100 text-gray-600">
                reviewed
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {stop.tenant_name} · {stop.lead?.status || 'unknown status'} · stopped{' '}
            {fmtET(stop.stopped_at)} {relTime(stop.stopped_at) && `(${relTime(stop.stopped_at)})`}
          </div>
        </div>
        <div className="flex-none text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{stop.failures.length}</span>{' '}
          <span className="text-xs text-gray-500">
            flag{stop.failures.length === 1 ? '' : 's'}
          </span>
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              What the monitor caught
            </div>
            <ul className="space-y-2">
              {stop.failures.map((f) => (
                <li key={f.id} className="text-sm">
                  <div className="font-medium text-gray-900">{f.rule}</div>
                  <div className="text-gray-600 whitespace-pre-wrap">{f.explanation}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{fmtET(f.created_at)}</div>
                </li>
              ))}
            </ul>
          </div>

          {stop.lead_id && !stop.lead_missing && (
            <Link
              to={`/lead/${stop.lead_id}`}
              className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Open the thread
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          )}
          {stop.lead_missing && (
            <div className="text-sm text-gray-500">
              This lead no longer exists. Nothing to act on; clear it.
            </div>
          )}

          {ack && (
            <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              Reviewed by <span className="font-medium">{ack.acknowledged_by || 'unknown'}</span> on{' '}
              {fmtET(ack.acknowledged_at)}
              {ack.acknowledgement_note ? <> · {ack.acknowledgement_note}</> : null}
            </div>
          )}

          <AckPanel stop={stop} onDone={onDone} />
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function QualityStops() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('pinned');

  const load = useCallback(async (state) => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetchWithCsrf(`/api/admin/quality-stops?state=${state}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setData(json);
    } catch (e) {
      setErr(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [load, filter]);

  const stops = data?.stops || [];

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Quality Stops</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Leads where the quality monitor caught the AI getting it wrong and shut it off.
            Admin only. Nothing here is visible to the tenant.
          </p>
        </div>
        <button
          onClick={() => load(filter)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {data && (
        <div className="flex flex-wrap gap-3">
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
            <div className="text-xs text-gray-500">Leads</div>
            <div className="text-lg font-semibold text-gray-900">{data.lead_count}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
            <div className="text-xs text-gray-500">Flags</div>
            <div className="text-lg font-semibold text-gray-900">{data.failure_count}</div>
          </div>
          <div className={`rounded-lg border px-3 py-2 ${
            data.still_off > 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'
          }`}>
            <div className="text-xs text-gray-500">AI still off</div>
            <div className={`text-lg font-semibold ${
              data.still_off > 0 ? 'text-amber-800' : 'text-gray-900'
            }`}>{data.still_off}</div>
          </div>
        </div>
      )}

      <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-sm font-medium ${
              filter === f.key ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {err && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
          Could not load quality stops: {err}
        </div>
      )}

      {loading && !data && <div className="text-sm text-gray-500">Loading...</div>}

      {data && stops.length === 0 && !err && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
          {filter === 'pinned' ? 'Nothing waiting on you.' : 'Nothing with this status.'}
        </div>
      )}

      <div className="space-y-2.5">
        {stops.map((s) => (
          <StopRow key={s.lead_id || s.failures[0]?.id} stop={s} onDone={() => load(filter)} />
        ))}
      </div>
    </div>
  );
}
